import React, { useEffect, useRef, useState } from "react";
import {
  Check,
  Maximize,
  Minimize,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Settings,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@cointervue/shared";
import { PLAYBACK_RATES, type PlayerControlsProps } from "./types";
import { formatTime } from "./utils";

export const PlayerControls: React.FC<PlayerControlsProps> = React.memo(({
  showControls,
  isPlaying,
  currentTime,
  duration,
  bufferedEnd,
  volume,
  isMuted,
  playbackRate,
  isFullscreen,
  isSpeedOpen,
  onTogglePlay,
  onSkip,
  onVolumeChange,
  onToggleMute,
  onRateChange,
  onSpeedOpenChange,
  onToggleFullscreen,
  onSeek,
  onScrubbingChange,
}) => {
  const progressTrackRef = useRef<HTMLDivElement>(null);
  const speedMenuRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ pos: number; time: number } | null>(
    null,
  );

  const safeDuration = duration > 0 ? duration : 0;
  const playedPercent =
    safeDuration > 0 ? Math.min(100, (currentTime / safeDuration) * 100) : 0;
  const bufferedPercent =
    safeDuration > 0 ? Math.min(100, (bufferedEnd / safeDuration) * 100) : 0;

  // Close speed menu when clicking outside
  useEffect(() => {
    if (!isSpeedOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        speedMenuRef.current &&
        !speedMenuRef.current.contains(e.target as Node)
      ) {
        onSpeedOpenChange(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onSpeedOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isSpeedOpen, onSpeedOpenChange]);

  const handleProgressSeek = (clientX: number) => {
    const track = progressTrackRef.current;
    if (!track || safeDuration <= 0) return;

    const rect = track.getBoundingClientRect();
    if (rect.width === 0) return;
    const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(pos * safeDuration);
  };

  // Cross-platform pointer events (Mouse, Touch, Pen)
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    onScrubbingChange(true);
    handleProgressSeek(e.clientX);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      handleProgressSeek(moveEvent.clientX);
    };

    const handlePointerUp = () => {
      onScrubbingChange(false);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  };

  const handlePointerHoverMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const track = progressTrackRef.current;
    if (!track || safeDuration <= 0) return;

    const rect = track.getBoundingClientRect();
    if (rect.width === 0) return;
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHover({
      pos: pos * 100,
      time: pos * safeDuration,
    });
  };

  const handlePointerLeave = () => {
    setHover(null);
  };

  return (
    <div
      onDoubleClick={(e) => e.stopPropagation()}
      className={cn(
        "absolute bottom-0 left-0 right-0 px-4 pb-3 bg-linear-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-300 z-20",
        showControls ? "opacity-100" : "opacity-0 pointer-events-none",
      )}
    >
      {/* Scrubber / Progress Bar */}
      <div
        ref={progressTrackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerHoverMove}
        onPointerLeave={handlePointerLeave}
        className="relative h-3 bg-white/20 rounded-full cursor-pointer transition-all duration-150 mb-3 group/track touch-none"
      >
        {/* Buffered Progress */}
        <div
          className="absolute top-0 bottom-0 left-0 bg-white/35 rounded-full pointer-events-none"
          style={{ width: `${bufferedPercent}%` }}
        />

        {/* Played Progress */}
        <div
          className="absolute top-0 bottom-0 left-0 bg-brand-dark-orange rounded-full pointer-events-none"
          style={{ width: `${playedPercent}%` }}
        />

        {/* Playhead Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-md scale-0 group-hover/track:scale-100 transition-transform pointer-events-none"
          style={{ left: `${playedPercent}%` }}
        />

        {/* Hover Time Tooltip */}
        {hover && (
          <div
            className="absolute -top-7 -translate-x-1/2 px-2 py-0.5 rounded bg-black/90 text-[11px] font-mono text-white border border-white/10 pointer-events-none shadow"
            style={{ left: `${hover.pos}%` }}
          >
            {formatTime(hover.time)}
          </div>
        )}
      </div>

      {/* Controls Toolbar */}
      <div className="flex items-center justify-between gap-2 text-white">
        {/* Left: Play/Pause, Rewind, Forward, Volume, Time */}
        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={onTogglePlay}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white transition cursor-pointer"
            title={isPlaying ? "Pause (Space)" : "Play (Space)"}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 fill-current" />
            ) : (
              <Play className="w-5 h-5 fill-current" />
            )}
          </button>

          <button
            type="button"
            onClick={() => onSkip(-10)}
            className="p-1.5 rounded-lg hover:bg-white/20 text-gray-200 hover:text-white transition cursor-pointer"
            title="Rewind 10 seconds (Left Arrow / J)"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => onSkip(10)}
            className="p-1.5 rounded-lg hover:bg-white/20 text-gray-200 hover:text-white transition cursor-pointer"
            title="Forward 10 seconds (Right Arrow / L)"
          >
            <RotateCw className="w-4 h-4" />
          </button>

          {/* Volume Control */}
          <div className="flex items-center gap-1.5 group/volume">
            <button
              type="button"
              onClick={onToggleMute}
              className="p-1.5 rounded-lg hover:bg-white/20 text-gray-200 hover:text-white transition cursor-pointer"
              title={isMuted ? "Unmute (M)" : "Mute (M)"}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="w-5 h-5 text-brand-error-red" />
              ) : volume < 0.5 ? (
                <Volume1 className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>

            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={isMuted ? 0 : volume}
              onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
              className="w-14 md:w-20 h-1.5 accent-brand-dark-orange bg-white/30 rounded-lg cursor-pointer"
              title="Volume"
            />
          </div>

          {/* Timestamp */}
          <div className="text-xs font-mono text-gray-200 pl-1">
            <span>{formatTime(currentTime)}</span>
            <span className="text-gray-400 mx-1">/</span>
            <span className="text-gray-400">{formatTime(safeDuration)}</span>
          </div>
        </div>

        {/* Right: Speed Menu (In-tree for Fullscreen compatibility), Fullscreen */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Speed Selector */}
          <div ref={speedMenuRef} className="relative">
            <button
              type="button"
              onClick={() => onSpeedOpenChange(!isSpeedOpen)}
              className="px-2 py-1 rounded-lg hover:bg-white/20 text-xs font-semibold text-gray-200 hover:text-white transition flex items-center gap-1 cursor-pointer"
              title="Playback Speed"
              aria-haspopup="true"
              aria-expanded={isSpeedOpen}
            >
              <Settings className="w-3.5 h-3.5" />
              <span>{playbackRate}x</span>
            </button>

            {isSpeedOpen && (
              <div className="absolute bottom-full right-0 mb-2 bg-neutral-900/95 border border-white/15 rounded-lg shadow-xl p-1 w-24 backdrop-blur-md z-50 animate-in fade-in-0 zoom-in-95">
                <div className="px-2 py-1 text-[10px] uppercase font-semibold text-gray-400 tracking-wider border-b border-white/10 mb-1">
                  Speed
                </div>
                {PLAYBACK_RATES.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => {
                      onRateChange(rate);
                      onSpeedOpenChange(false);
                    }}
                    className={cn(
                      "w-full px-2.5 py-1 text-xs text-left flex items-center justify-between rounded hover:bg-white/15 transition cursor-pointer",
                      playbackRate === rate
                        ? "text-brand-dark-orange font-semibold"
                        : "text-gray-200",
                    )}
                  >
                    <span>{rate}x</span>
                    {playbackRate === rate && (
                      <Check className="w-3.5 h-3.5 text-brand-dark-orange" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fullscreen Button */}
          <button
            type="button"
            onClick={onToggleFullscreen}
            className="p-1.5 rounded-lg hover:bg-white/20 text-gray-200 hover:text-white transition cursor-pointer"
            title={isFullscreen ? "Exit Fullscreen (F)" : "Fullscreen (F)"}
          >
            {isFullscreen ? (
              <Minimize className="w-4 h-4" />
            ) : (
              <Maximize className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

PlayerControls.displayName = "PlayerControls";
